import { earliestStartAfterRest } from "../../../domain/adjoiningRest";
import type { AdjoiningRestLimits } from "../../../domain/adjoiningRest";
import { analyzeBreaks, requiredBreakSeconds } from "../../../domain/breakRule";
import type { ClockState } from "../../../domain/clock";
import type { EpochSeconds, LocalDate } from "../../../domain/time";
import type { WorkDay } from "../../../domain/workday";
import type { LocalTimeline } from "../../../offline/localTimeline";
import { runningBreakQualification } from "./clockActions";
import type { ClockLimits } from "./clockLimits";

/** Lead time of the "break due" warning (spec 7.3.1: 15 min before 6 h without a break). */
export const breakWarningLeadSeconds = 900;

/** Lead time of the warning before the absolute daily maximum. */
export const dailyLimitLeadSeconds = 1_800;

/** A live warning on the clock screen. */
export type ClockWarning =
  | { readonly kind: "breakDue"; readonly inSeconds: number }
  | { readonly kind: "breakOverdue"; readonly overSeconds: number }
  | {
      readonly kind: "breakMissing";
      readonly requiredSeconds: number;
      readonly takenSeconds: number;
    }
  | { readonly kind: "dailyLimitSoon"; readonly inSeconds: number; readonly limitSeconds: number }
  | { readonly kind: "dailyLimitExceeded"; readonly limitSeconds: number }
  | { readonly kind: "regularLimitExceeded"; readonly limitSeconds: number }
  | { readonly kind: "restActive"; readonly until: EpochSeconds }
  | { readonly kind: "openDays"; readonly dates: readonly LocalDate[] };

/** Input of {@link computeClockWarnings}. */
export interface ClockWarningInput {
  readonly state: ClockState;
  /** Current working day (open, or the day of today), if any. */
  readonly day: WorkDay | null;
  readonly now: EpochSeconds;
  readonly limits: ClockLimits;
  /** Earliest start after the minimum rest, if a rest period is running. */
  readonly earliestStart: EpochSeconds | null;
  /** Days still open on the server that need a correction (`openDaysToCorrect`, F-11). */
  readonly earlierOpenDays: readonly LocalDate[];
}

/**
 * Computes the live warnings of the clock screen. The rules match the compliance engine; the
 * server stays authoritative and reports findings after synchronization.
 *
 * @param input - Current state.
 * @returns Warnings, most urgent first.
 */
export function computeClockWarnings(input: ClockWarningInput): ClockWarning[] {
  const warnings: ClockWarning[] = [];
  const { day, limits } = input;

  if (input.earlierOpenDays.length > 0) {
    warnings.push({ kind: "openDays", dates: input.earlierOpenDays });
  }

  if (input.state === "Off" && input.earliestStart !== null && input.now < input.earliestStart) {
    warnings.push({ kind: "restActive", until: input.earliestStart });
  }

  if (day === null || input.state === "Off") {
    return warnings;
  }

  if (day.workSeconds > limits.dailyAbsoluteMaxSeconds) {
    warnings.push({ kind: "dailyLimitExceeded", limitSeconds: limits.dailyAbsoluteMaxSeconds });
  } else if (day.workSeconds >= limits.dailyAbsoluteMaxSeconds - dailyLimitLeadSeconds) {
    warnings.push({
      kind: "dailyLimitSoon",
      inSeconds: limits.dailyAbsoluteMaxSeconds - day.workSeconds,
      limitSeconds: limits.dailyAbsoluteMaxSeconds,
    });
  }

  const analysis = analyzeBreaks(day, limits.break.minBreakSegmentSeconds);
  // Standby and waiting time is no rest break: the stretch keeps running (ADR-0092).
  const onStandby = input.state === "OnBreak" && runningBreakQualification(day) === "Standby";

  if (input.state === "Working" || onStandby) {
    const remaining = limits.break.maxContinuousWorkSeconds - analysis.currentStretchSeconds;

    if (remaining < 0) {
      warnings.push({ kind: "breakOverdue", overSeconds: -remaining });
    } else if (remaining <= breakWarningLeadSeconds) {
      warnings.push({ kind: "breakDue", inSeconds: remaining });
    }
  }

  const required = requiredBreakSeconds(analysis.dutySeconds, limits.break.thresholds);

  if (required > 0 && analysis.qualifyingBreakSeconds < required) {
    warnings.push({
      kind: "breakMissing",
      requiredSeconds: required,
      takenSeconds: analysis.qualifyingBreakSeconds,
    });
  }

  if (
    day.workSeconds > limits.dailyRegularMaxSeconds &&
    day.workSeconds <= limits.dailyAbsoluteMaxSeconds - dailyLimitLeadSeconds
  ) {
    warnings.push({ kind: "regularLimitExceeded", limitSeconds: limits.dailyRegularMaxSeconds });
  }

  return warnings;
}

/** What the clock warning needs to keep the rest around a Sunday that has passed (§ 11 (4) ArbZG, F-53). */
export interface AdjoiningRestContext {
  /** The current instant. */
  readonly now: EpochSeconds;
  /** The limits of the rest around a rest day. */
  readonly limits: AdjoiningRestLimits;
}

/**
 * Computes the earliest regular start after the last closed day (spec 7.3.3) from the server
 * forecast and the local timeline; the later instant wins (stricter). With `adjoining`, a Sunday
 * that has passed since then needs the daily rest next to it (F-53). Public holidays come only with
 * the server forecast; neither forecast knows substitute rest days or documented exceptions, so the
 * warning can be stricter than the check. A deployment during on-call duty is clocked, so the last
 * clock-out ends the rest as the server rule does; the end of counted on-site standby comes with the
 * server forecast; the shortening of § 5 (3) ArbZG is never offered (F-113, ADR-0233).
 *
 * @param timeline - Local timeline.
 * @param serverEarliest - Forecast of the server, if known.
 * @param minRestSeconds - Minimum rest.
 * @param adjoining - Current instant and limits of the rest around a rest day, if checked.
 * @returns The instant or `null`.
 */
export function earliestRegularStart(
  timeline: LocalTimeline,
  serverEarliest: EpochSeconds | null,
  minRestSeconds: number,
  adjoining: AdjoiningRestContext | null = null,
): EpochSeconds | null {
  const lastClosed = [...timeline.days].reverse().find((day) => day.isClosed);
  const closedAt = lastClosed?.closedAt ?? null;
  const local = closedAt === null ? null : closedAt + minRestSeconds;
  const afterRestDays =
    closedAt === null || lastClosed === undefined || adjoining === null
      ? null
      : earliestStartAfterRest(
          closedAt,
          adjoining.now,
          new Set(),
          lastClosed.timeZoneId,
          adjoining.limits,
        );
  const candidates = [local, afterRestDays, serverEarliest].filter(
    (value): value is number => value !== null,
  );
  const earliest = candidates.length === 0 ? null : Math.max(...candidates);

  return earliest;
}
